<template>
  <div id="app" style="position: relative">
    <Toast position="bottom-right" group="br" />
    <div class="d-flex justify-content-center" style="min-height: 100vh">
      <div
        v-if="!isOpenSite && !isMobile && user"
        class="vh-100"
        style="background-color: #f2eee9"
      >
        <Card class="py-3" id="user-nav-menu-card">
          <template #content>
            <div
              class="overflow-scroll"
              style="max-height: calc(100vh - 1.5rem)"
            >
              <Button
                v-if="!isMobile"
                type="button"
                icon="pi pi-user"
                @click="toggle"
                outlined
                rounded
                aria-haspopup="true"
                aria-controls="overlay_menu"
                class="text-black border-0"
              >
                <div
                  class="d-flex align-items-center"
                  style="font-size: 0.8rem; padding: 0rem 0.5rem; color: black"
                >
                  <i class="pi pi-user" style="margin-right: 0.5rem"></i>
                  <span>{{ user.email }}</span>
                </div>
              </Button>

              <Menu ref="menu" id="overlay_menu" :model="userMenuItems" popup />

              <PanelMenu
                v-model:expanded-keys="expanded"
                :model="headerItems"
                class="without-border"
                id="user-nav-menu-tiered"
              >
                <template #item="{ item }">
                  <a
                    class="d-flex align-items-center justify-content-between px-2 py-2 group w-100 list-item"
                    style="font-size: 0.7rem"
                  >
                    <div class="d-flex align-items-center" role="button">
                      <span
                        v-if="item.icon"
                        :class="[
                          item.icon,
                          'pi text-primary group-hover:text-inherit me-1',
                        ]"
                        style="color: black !important; font-size: 0.7rem"
                      />
                      <span
                        :class="['ms-1', { 'font-semibold': item.items }]"
                        style="color: black; font-size: 0.7rem"
                        >{{ item.label }}
                      </span>
                      <Badge
                        v-if="item.badge"
                        class="ms-2"
                        severity="danger"
                        :value="item.badge"
                        style="font-size: 0.7rem"
                      />
                    </div>
                    <i
                      v-if="item.items"
                      class="pi pi-angle-down"
                      style="font-size: 0.7rem; color: black !important"
                    ></i>
                  </a>
                </template>
              </PanelMenu>
            </div>
          </template>
        </Card>
      </div>
      <div
        class="contents w-100"
        :class="{
          'is-open-site': isOpenSite,
        }"
        style="position: relative"
      >
        <transition name="slide" mode="out-in">
          <PageHeader
            v-if="isOpenSite"
            :page-title="pageTitle"
            :user="user"
            :organizations="[association, ...branchs]"
            :latest-magazine="latestMagazine"
            :is-open-site="isOpenSite"
          />
        </transition>
        <transition name="slide" mode="out-in">
          <OpenSiteHeader
            v-if="isOpenSite && currentRouteName != 'Index'"
            :title="pageTitle"
            :subtitle="pageSubtitle"
            class="mt-3"
            :key="currentRouteName || '' + currentRouteQuery"
          />
        </transition>
        <transition name="slide" mode="out-in">
          <OpenSitePageTree
            v-if="
              isOpenSite &&
              (pageTree || overwritePageTree) &&
              currentRouteName != 'Index'
            "
            :page-tree="overwritePageTree || pageTree"
            :key="currentRouteName || '' + currentRouteQuery"
            style="position: sticky; top: 34px; z-index: 500"
          />
        </transition>

        <div
          class="d-flex w-100 flex-column align-items-center"
          :class="{
            'pb-3': isOpenSite,
          }"
          :style="{
            height: !isOpenSite ? '100%' : 'auto',
            'min-height': !isOpenSite ? '100%' : 'auto',
          }"
        >
          <div
            class="router-view d-flex flex-column align-items-center"
            :class="{
              'is-mobile': isMobile,
            }"
            :style="{
              width: '100%',
              height: '100%',
              'min-height': '100%',
            }"
          >
            <router-view
              v-slot="{ Component }"
              style="width: 100%; height: 100%"
              :style="{
                'min-height': !isOpenSite ? '100%' : '400px',
              }"
              :class="{
                'mb-3': isOpenSite,
              }"
            >
              <transition name="slide" mode="out-in">
                <component :is="Component" :key="$route.path" />
              </transition>
            </router-view>

            <div
              v-if="isOpenSite"
              class="w-100"
              style="background-color: #f2eee9"
              id="footer"
            >
              <div
                :class="{
                  'position-relative': scrollTopBtn?.showBtn,
                }"
              >
                <div class="position-absolute" style="top: -70px; right: 0">
                  <ScrollTopButton ref="scrollTopBtn" />
                </div>
              </div>
              <PageFooter class="w-100" :organizations="branchs" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import {
  useRouterUtil,
  useMqUtils,
  useUser,
  useOrganizations,
  useMagazines,
  useAuthorize,
} from "/@/vue/composables";
import { PageHeader, PageFooter } from "/@/vue/components/Layouts";
import {
  OpenSiteHeader,
  OpenSitePageTree,
  ScrollTopButton,
} from "/@/vue/components/Molecules";
import Toast from "primevue/toast";
import Card from "primevue/card";
import PanelMenu from "primevue/panelmenu";
import Menu from "primevue/menu";
import Button from "primevue/button";
import Badge from "primevue/badge";

const {
  currentRouteName,
  currentRouteMeta,
  currentRouteQuery,
  isOpenSite,
  goto,
  reload,
} = useRouterUtil();

const pageTitle = computed(() => {
  return currentRouteMeta.value?.title;
});
const pageSubtitle = computed(() => {
  return currentRouteMeta.value?.subtitle;
});

const pageTree = computed(() => {
  return currentRouteMeta.value?.tree;
});

const recruitmentType = computed(() => {
  return currentRouteQuery.value?.recruitmentType;
});

const isInvest24 = computed(() => {
  return recruitmentType.value === "invest24" || !recruitmentType.value;
});

const overwritePageTree = computed(() => {
  if (currentRouteName.value === "Invest24JoinUsersNew") {
    return [
      {
        title: isInvest24.value
          ? "奥能登実態把調査ボランティア"
          : "1.5次避難所ボランティア",
        name: isInvest24.value ? "Invest24Index" : "Shelter24Index",
      },
      {
        title: "ボランティア申請",
      },
    ];
  }
});

const { isMobile } = useMqUtils();

const { getUser, headerItems, setUser } = useUser();
const { data: user } = getUser();

watch(
  () => user.value,
  (newUser) => {
    if (newUser) {
      setUser(newUser);
    }
  }
);

const { getOrganizations } = useOrganizations();
const { data: organizations } = getOrganizations({
  association: true,
  branch: true,
});

const association = computed(() => {
  if (!organizations.value) return undefined;

  return organizations.value.find((org) => !org.branch && !org.committee);
});

const branchs = computed(() => {
  if (!organizations.value) return [];

  return organizations.value.filter((org) => org.branch && !org.committee);
});

// magazines

const { getMagazines } = useMagazines();
const { data: magazines } = getMagazines({
  onlyLatest: true,
});
const latestMagazine = computed(() => {
  if (!magazines.value) return undefined;

  return magazines.value[0];
});

// header items

const menu = ref();
const toggle = (event) => {
  if (!user.value) {
    goto({ name: "SignIn" });
    return;
  }

  menu.value.toggle(event);
};

const { logout } = useAuthorize();

async function handleLogout() {
  await logout();
  reload();
}

const userMenuItems = computed(() => {
  return [
    {
      label: user.value?.email,
      items: [
        {
          to: {
            name: "SignIn",
          },
          label: "ログイン",
          icon: "pi pi-minus",
          visible: !user.value,
          command: () => {
            goto({ name: "SignIn" });
          },
        },
        {
          to: {
            name: "UsersShow",
          },
          label: "マイページ",
          icon: "pi pi-minus",
          visible: !!user.value,
          command: () => {
            goto({ name: "UsersShow" });
          },
        },
        {
          label: "ログアウト",
          icon: "pi pi-minus",
          command: async () => {
            await logout();
            reload();
          },
          visible: !!user.value,
        },
      ],
    },
  ];
});

const expanded = ref<{ [key: string]: boolean }>({
  UsersSystemAdminMenu: true,
  UsersAdminContents: true,
});

//

const scrollTopBtn = ref();

onMounted(() => {
  function callback(mutationsList: MutationRecord[], obs: MutationObserver) {
    // TODO:
    // primevue で追加される panel の filterinput に対して Enter キーで閉じててしまう問題への対処
    // 以下は Dropdown で開かれる panel 内の filterinput にのみ対応している
    //

    for (const mutation of mutationsList) {
      const panel = Array.from(mutation.addedNodes).find(
        (node) => node?.dataset?.pcSection === "panel"
      );

      if (panel) {
        const header = Array.from(panel.childNodes).find(
          (node) => node?.dataset?.pcSection === "header"
        );

        if (header) {
          const filtercontainer = Array.from(header.childNodes).find(
            (node) => node?.dataset?.pcSection === "filtercontainer"
          );

          if (filtercontainer) {
            const filterinput = Array.from(filtercontainer.childNodes).find(
              (node) => node?.dataset?.pcSection === "filterinput"
            );

            if (filterinput) {
              filterinput.addEventListener(
                "keydown",
                (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                },
                true
              );
            }
          }
        }
      }
    }
  }

  const target = document.body;
  const obs = new MutationObserver(callback);
  obs.observe(target, { childList: true });
});
</script>

<style lang="scss">
.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  // transform:translate(-30%)
}

#user-nav-menu-card {
  box-shadow: none;
  padding-top: 0;
  // background-color: #f2f3ec;
  background-color: transparent;
  border-radius: 0;

  .p-card-body {
    padding: 0 0.5rem 1rem;
  }

  .p-tieredmenu {
    border: none;
    background-color: transparent;
  }

  .p-panelmenu {
    font-size: 0.7rem;
  }

  .p-panelmenu {
    .p-panelmenu-content {
      .p-menuitem > .p-menuitem-content {
        color: black;

        .p-menuitem-link {
          color: black;

          .p-menuitem-icon,
          .p-menuitem-text,
          .p-submenu-icon {
            color: black;
          }
        }
      }
    }

    .p-panelmenu-header {
      .p-panelmenu-header-content {
        .p-panelmenu-header-action {
          .p-submenu-icon,
          .p-menuitem-text {
            color: black;
          }
        }
      }
    }

    .p-submenu-list {
      .list-item {
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
      }
    }
  }

  .p-panelmenu-panel {
    border: none;
    margin-bottom: 0;

    .p-panelmenu-content {
      background: transparent;
    }
  }

  .p-card-body {
    background-color: transparent;
  }
}

#app {
  letter-spacing: 0.1rem;
  min-height: 100vh;

  .is-open-site {
    color: $main-brawn;
  }

  .router-view {
    position: relative;
    height: 100%;

    > * {
      padding: 1rem 3rem;
      min-height: calc(100vh - 300px);
    }

    #footer {
      min-height: auto;
    }

    &.is-mobile {
      padding-top: 0;

      > * {
        padding: 1rem;
      }
    }
  }
}
</style>
